import { lazy } from "react";
import {
  Layout, MessageSquare, FileText, Calendar, Briefcase, Settings,
  Workflow, Activity, FileCode, Clock, KanbanSquare,
  Share2, Users, Wrench, Megaphone, Target, Utensils, CalendarRange, Star, Compass, Mail, Library,
} from "lucide-react";

/**
 * Single source of truth for Margo's dashboard route table.
 *
 * Each entry maps a stable tab id (the value that used to live in App's
 * `activeTab` state + capabilities.allowed_tabs) to a URL path and a lazily
 * loaded page component. The sidebar nav, the React Router config, the command
 * palette, and the legacy `?tab=` redirect all derive from this one list so
 * they can never drift.
 *
 * Faithful refactor of the old 885-line monolith: every tab the render map
 * handled is preserved here, including `campaigns_ops` + `hosted_dines` which
 * were rendered but were missing from the flat NAV_ITEMS list.
 *
 * `Workflow` is the brand mark in the shell; kept here so the icon set stays
 * colocated.
 */

export type ExecTab =
  | "overview"
  | "conversations"
  | "documents"
  | "calendar"
  | "departments"
  | "scheduled"
  | "marketing_ops"
  | "campaigns_ops"
  | "hosted_dines"
  | "contacts"
  | "tools"
  | "events_sales"
  | "reviews"
  | "activity"
  | "social"
  | "email_studio"
  | "content_library"
  | "reports"
  | "files"
  | "careers"
  | "settings";

export interface RouteSpec {
  /** Stable tab id — matches capabilities.allowed_tabs + legacy ?tab= values. */
  id: ExecTab;
  /** URL path (no leading slash collision; used as <Route path>). */
  path: string;
  label: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  /** Lazy page component (React.lazy default-export). */
  Component: React.LazyExoticComponent<React.ComponentType>;
}

export const ROUTES: RouteSpec[] = [
  { id: "overview",       path: "overview",      label: "Overview",      icon: Layout,         Component: lazy(() => import("./pages/OverviewPage")) },
  { id: "conversations",  path: "conversations", label: "Conversations", icon: MessageSquare,  Component: lazy(() => import("./pages/ConversationsPage")) },
  { id: "activity",       path: "activity",      label: "Activity",      icon: KanbanSquare,   Component: lazy(() => import("./pages/ActivityPage")) },
  { id: "social",         path: "social",        label: "Social Media",  icon: Share2,         Component: lazy(() => import("./pages/SocialPage")) },
  { id: "email_studio",   path: "email-studio",  label: "Email Studio",  icon: Mail,           Component: lazy(() => import("./pages/EmailStudioPage")) },
  { id: "content_library", path: "content-library", label: "Content Library", icon: Library,    Component: lazy(() => import("./pages/ContentLibraryPage")) },
  { id: "documents",      path: "documents",     label: "Documents",     icon: FileText,       Component: lazy(() => import("./pages/DocumentsPage")) },
  { id: "calendar",       path: "calendar",      label: "Calendar",      icon: Calendar,       Component: lazy(() => import("./pages/CalendarPage")) },
  { id: "scheduled",      path: "scheduled",     label: "Scheduled",     icon: Clock,          Component: lazy(() => import("./pages/ScheduledPage")) },
  { id: "marketing_ops",  path: "marketing",     label: "Marketing Ops", icon: Megaphone,      Component: lazy(() => import("./pages/MarketingOpsPage")) },
  { id: "campaigns_ops",  path: "campaigns",     label: "Campaigns",     icon: Target,         Component: lazy(() => import("./pages/CampaignsPage")) },
  { id: "hosted_dines",   path: "hosted-dines",  label: "Hosted Dines",  icon: Utensils,       Component: lazy(() => import("./pages/HostedDinesPage")) },
  { id: "contacts",       path: "contacts",      label: "Contacts",      icon: Users,          Component: lazy(() => import("./pages/ContactsPage")) },
  { id: "events_sales",   path: "events-sales",  label: "Events Sales",  icon: CalendarRange,  Component: lazy(() => import("./pages/EventsSalesPage")) },
  { id: "reviews",        path: "reviews",       label: "Reviews",       icon: Star,           Component: lazy(() => import("./pages/ReviewsPage")) },
  { id: "tools",          path: "tools",         label: "Tools",         icon: Wrench,         Component: lazy(() => import("./pages/ToolsPage")) },
  { id: "departments",    path: "departments",   label: "Departments",   icon: Briefcase,      Component: lazy(() => import("./pages/DepartmentsPage")) },
  { id: "reports",        path: "reports",       label: "Reports",       icon: Activity,       Component: lazy(() => import("./pages/ReportsPage")) },
  { id: "files",          path: "files",         label: "Files",         icon: FileCode,       Component: lazy(() => import("./pages/FilesPage")) },
  { id: "careers",        path: "careers",       label: "Careers",       icon: Compass,        Component: lazy(() => import("./pages/CareersPage")) },
  { id: "settings",       path: "settings",      label: "Settings",      icon: Settings,       Component: lazy(() => import("./pages/SettingsPage")) },
];

/** The icon used for the sidebar brand mark. */
export const BRAND_ICON = Workflow;

/** tab id → path (no leading slash). */
export const TAB_TO_PATH: Record<string, string> = Object.fromEntries(
  ROUTES.map((r) => [r.id, r.path])
);

/** path → tab id (for reverse lookups / active-state). */
export const PATH_TO_TAB: Record<string, ExecTab> = Object.fromEntries(
  ROUTES.map((r) => [r.path, r.id])
);

/** The default landing route. */
export const DEFAULT_TAB: ExecTab = "overview";

/**
 * Grouped sidebar IA — marketing-ops oriented. Items reference tab ids; the
 * shell resolves them to RouteSpecs and renders NavLinks inside collapsible
 * sections. Every one of Margo's 19 tabs appears in exactly one section.
 */
export const NAV_SECTIONS: Array<{ label: string; items: ExecTab[] }> = [
  {
    label: "Workspace",
    items: ["overview", "conversations", "activity", "calendar", "contacts", "careers"],
  },
  {
    label: "Content & Social",
    items: ["social", "email_studio", "content_library", "documents", "marketing_ops", "tools"],
  },
  {
    label: "Campaigns & Events",
    items: ["campaigns_ops", "events_sales", "hosted_dines", "reviews"],
  },
  {
    label: "Operations",
    items: ["scheduled", "departments", "reports", "files"],
  },
  {
    label: "System",
    items: ["settings"],
  },
];
